import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import FeedbackBanner from '../../components/feature/FeedbackBanner';
import SEOHead from '../../components/seo/SEOHead';
import SchemaOrg from '../../components/seo/SchemaOrg';

export default function ArticleFimCiapPisCofins() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title="Fim do CIAP do PIS/COFINS com a Reforma Tributária? | Análise Técnica Completa"
        description="Análise técnica profunda sobre o futuro do CIAP (Controle de Crédito de ICMS do Ativo Permanente) no contexto da reforma tributária: extinção do PIS/COFINS, transição para CBS, tratamento de créditos, ativos em andamento e impactos práticos para empresas."
        keywords="CIAP, PIS, COFINS, reforma tributária, CBS, crédito tributário, ativo imobilizado, apropriação de créditos, extinção PIS COFINS, transição tributária"
        canonical="https://ativonews.com.br/artigo/fim-ciap-pis-cofins-reforma-tributaria"
      />
      <SchemaOrg type="Article" data={{
        headline: "Fim do CIAP do PIS/COFINS com a Reforma Tributária?",
        description: "Análise técnica sobre o futuro do CIAP no contexto da reforma tributária e a transição do PIS/COFINS para CBS",
        author: "Dr. Fernando Silva",
        datePublished: "2025-01-22",
        image: "https://public.readdy.ai/ai/img_res/976ff2a9cc2ef94b4a730e9457408378.jpg"
      }} />
      
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <div className="w-full bg-gradient-to-b from-[#F8FAFC] to-white">
          <div className="max-w-7xl mx-auto px-6 pt-32 pb-16">
            <nav className="flex items-center gap-2 text-sm text-[#64748B] mb-8">
              <Link to="/" className="hover:text-[#1E40AF] transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line text-gray-400"></i>
              <Link to="/categoria/tributario" className="hover:text-[#1E40AF] transition-colors">
                Tributário
              </Link>
              <i className="ri-arrow-right-s-line text-gray-400"></i>
              <span className="text-[#0F172A] font-medium">CIAP e Reforma</span>
            </nav>

            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-[#DC2626] text-white text-sm font-bold rounded-lg uppercase tracking-wide">
                Tributário
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6 leading-tight max-w-4xl">
              Fim do CIAP do PIS/COFINS com a Reforma Tributária?
            </h1>

            <p className="text-xl text-[#475569] mb-8 leading-relaxed max-w-3xl">
              Análise técnica completa sobre o futuro do CIAP no contexto da extinção do PIS/COFINS e implementação da CBS: transição, créditos em andamento e impactos práticos.
            </p>

            <div className="flex flex-wrap items-center gap-6 text-[#64748B]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-gray-400"></i>
                </div>
                <div>
                  <div className="font-semibold text-[#0F172A]">Dr. Fernando Silva</div>
                  <div className="text-sm">Especialista em Tributação Federal</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                <span>22 de janeiro de 2025</span>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                <span>18 min</span>
              </div>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        <div className="w-full h-[500px] bg-gray-100">
          <img 
            src="https://readdy.ai/api/search-image?query=professional%20accountant%20analyzing%20tax%20credits%20CIAP%20documents%20financial%20planning%20spreadsheets%20modern%20office%20workspace%20tax%20reform%20strategy%20business%20planning&width=1200&height=500&seq=ciap-featured&orientation=landscape"
            alt="CIAP do PIS/COFINS e Reforma Tributária"
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Content Area */}
        <div className="w-full bg-white py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-12 gap-12">
              
              {/* Main Content */}
              <article className="lg:col-span-8">
                <div className="prose prose-lg max-w-none">
                  
                  {/* TL;DR */}
                  <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-10 rounded-r-lg">
                    <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <i className="ri-flashlight-line text-red-600"></i>
                      TL;DR — Resumo Executivo
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      Com a extinção gradual do PIS/COFINS e substituição pela CBS, o CIAP em sua forma atual deixará de existir. <strong>Os créditos em andamento serão preservados</strong> durante o período de transição (2027-2033). A CBS adotará modelo de creditamento pleno e imediato, eliminando a necessidade de apropriação parcelada em 48 meses. Empresas com ativos em aquisição precisam planejar a transição, revisar saldos de CIAP, documentar créditos pendentes e acompanhar as regulamentações sobre o tratamento dos créditos residuais.
                    </p>
                  </div>

                  {/* Introdução */}
                  <section className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">1. Introdução</h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      A Reforma Tributária aprovada pela Emenda Constitucional nº 132/2023 e regulamentada pela Lei Complementar nº 214/2025 representa a mais profunda transformação do sistema tributário brasileiro em décadas. Entre as mudanças estruturais, destaca-se a <strong>extinção gradual do PIS e da COFINS</strong>, que serão substituídos pela nova <strong>Contribuição sobre Bens e Serviços (CBS)</strong>.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Essa transição traz uma questão crucial para milhares de empresas brasileiras: <strong>o que acontecerá com o CIAP (Controle de Crédito de ICMS do Ativo Permanente) do PIS/COFINS?</strong>
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Este artigo analisa de forma técnica e abrangente todos os aspectos relacionados ao fim do CIAP e do PIS/COFINS, incluindo o período de transição, tratamento dos créditos em andamento, impactos práticos e orientações estratégicas para empresas.
                    </p>
                  </section>

                  {/* O que é o CIAP */}
                  <section className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">2. O que é o CIAP do PIS/COFINS?</h2>
                    
                    <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-6 rounded-r-lg">
                      <h3 className="text-lg font-bold text-gray-900 mb-3">Conceito Fundamental</h3>
                      <p className="text-gray-700 leading-relaxed">
                        O CIAP é um instrumento de controle utilizado para a apropriação de créditos de PIS e COFINS sobre aquisições de bens do ativo imobilizado. Diferentemente de outros créditos que podem ser apropriados integralmente no momento da aquisição, os créditos sobre ativos permanentes devem ser apropriados de forma <strong>parcelada em 48 meses</strong>.
                      </p>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-4 mt-8">Base Legal Atual</h3>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-red-600 text-xl mt-1"></i>
                        <span className="text-gray-700"><strong>Lei nº 10.637/2002</strong> (PIS não-cumulativo) - Art. 3º, §1º, inciso VI</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-red-600 text-xl mt-1"></i>
                        <span className="text-gray-700"><strong>Lei nº 10.833/2003</strong> (COFINS não-cumulativa) - Art. 3º, §1º, inciso VI</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-red-600 text-xl mt-1"></i>
                        <span className="text-gray-700"><strong>Instrução Normativa RFB nº 1.911/2019</strong> - Regulamentação detalhada</span>
                      </li>
                    </ul>

                    <h3 className="text-xl font-bold text-gray-900 mb-4 mt-8">Mecânica de Apropriação</h3>
                    <div className="bg-gray-50 p-6 rounded-lg mb-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-bold text-gray-900 mb-3">📊 Cálculo Mensal</h4>
                          <p className="text-gray-700 text-sm leading-relaxed">
                            Crédito Mensal = (Valor do Crédito Total ÷ 48) × (Receitas Tributadas ÷ Receita Total)
                          </p>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 mb-3">⏱️ Prazo</h4>
                          <p className="text-gray-700 text-sm leading-relaxed">
                            48 meses a partir do mês em que o bem for colocado em operação
                          </p>
                        </div>
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-4 mt-8">Ativos Sujeitos ao CIAP</h3>
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <h4 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                          <i className="ri-check-line"></i>
                          Incluídos
                        </h4>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li>• Máquinas e equipamentos</li>
                          <li>• Edificações e benfeitorias</li>
                          <li>• Veículos</li>
                          <li>• Móveis e utensílios</li>
                          <li>• Equipamentos de informática</li>
                        </ul>
                      </div>
                      <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                        <h4 className="font-bold text-red-900 mb-3 flex items-center gap-2">
                          <i className="ri-close-line"></i>
                          Excluídos
                        </h4>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li>• Terrenos</li>
                          <li>• Bens de valor &lt; R$ 5.000</li>
                          <li>• Bens usados</li>
                          <li>• Ativos intangíveis</li>
                          <li>• Bens para revenda</li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* Extinção do PIS/COFINS */}
                  <section className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">3. A Extinção do PIS/COFINS e o Nascimento da CBS</h2>
                    
                    <p className="text-gray-700 leading-relaxed mb-6">
                      A Reforma Tributária estabelece um <strong>período de transição gradual</strong> entre 2027 e 2033, durante o qual o PIS/COFINS será progressivamente substituído pela CBS (Contribuição sobre Bens e Serviços).
                    </p>

                    <h3 className="text-xl font-bold text-gray-900 mb-4">Cronograma de Transição</h3>
                    <div className="overflow-x-auto mb-8">
                      <table className="w-full border-collapse bg-white">
                        <thead>
                          <tr className="bg-red-600 text-white">
                            <th className="border border-gray-300 px-4 py-3 text-left font-bold">Ano</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-bold">PIS/COFINS</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-bold">CBS</th>
                            <th className="border border-gray-300 px-4 py-3 text-left font-bold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-4 py-3 font-bold">2026</td>
                            <td className="border border-gray-300 px-4 py-3">100%</td>
                            <td className="border border-gray-300 px-4 py-3">0%</td>
                            <td className="border border-gray-300 px-4 py-3 text-gray-600">Sistema atual</td>
                          </tr>
                          <tr className="hover:bg-gray-50 bg-yellow-50">
                            <td className="border border-gray-300 px-4 py-3 font-bold">2027</td>
                            <td className="border border-gray-300 px-4 py-3">90%</td>
                            <td className="border border-gray-300 px-4 py-3">10%</td>
                            <td className="border border-gray-300 px-4 py-3 text-yellow-700">Início da transição</td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-4 py-3 font-bold">2028</td>
                            <td className="border border-gray-300 px-4 py-3">80%</td>
                            <td className="border border-gray-300 px-4 py-3">20%</td>
                            <td className="border border-gray-300 px-4 py-3 text-gray-600">Transição</td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-4 py-3 font-bold">2029</td>
                            <td className="border border-gray-300 px-4 py-3">70%</td>
                            <td className="border border-gray-300 px-4 py-3">30%</td>
                            <td className="border border-gray-300 px-4 py-3 text-gray-600">Transição</td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-4 py-3 font-bold">2030</td>
                            <td className="border border-gray-300 px-4 py-3">50%</td>
                            <td className="border border-gray-300 px-4 py-3">50%</td>
                            <td className="border border-gray-300 px-4 py-3 text-gray-600">Meio da transição</td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-4 py-3 font-bold">2031</td>
                            <td className="border border-gray-300 px-4 py-3">30%</td>
                            <td className="border border-gray-300 px-4 py-3">70%</td>
                            <td className="border border-gray-300 px-4 py-3 text-gray-600">Transição</td>
                          </tr>
                          <tr className="hover:bg-gray-50 bg-green-50">
                            <td className="border border-gray-300 px-4 py-3 font-bold">2032</td>
                            <td className="border border-gray-300 px-4 py-3">10%</td>
                            <td className="border border-gray-300 px-4 py-3">90%</td>
                            <td className="border border-gray-300 px-4 py-3 text-green-700">Final da transição</td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-4 py-3 font-bold">2033</td>
                            <td className="border border-gray-300 px-4 py-3">0%</td>
                            <td className="border border-gray-300 px-4 py-3">100%</td>
                            <td className="border border-gray-300 px-4 py-3 text-gray-600">Novo sistema</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="bg-amber-50 border-l-4 border-amber-600 p-6 rounded-r-lg">
                      <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-alert-line text-amber-600"></i>
                        Importante
                      </h4>
                      <p className="text-gray-700 leading-relaxed">
                        Durante todo o período de transição (2027-2033), as empresas deverão calcular e recolher <strong>simultaneamente</strong> o PIS/COFINS (em percentual decrescente) e a CBS (em percentual crescente). Isso significa que o CIAP continuará sendo utilizado para a parcela de PIS/COFINS ainda vigente.
                      </p>
                    </div>
                  </section>

                  {/* O Fim do CIAP */}
                  <section className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">4. O Fim do CIAP: Quando e Como?</h2>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-4">4.1. Extinção Gradual</h3>
                    <p className="text-gray-700 leading-relaxed mb-6">
                      O CIAP não será extinto de forma abrupta. Sua extinção seguirá o mesmo cronograma de transição do PIS/COFINS para a CBS:
                    </p>

                    <div className="grid md:grid-cols-3 gap-4 mb-8">
                      <div className="bg-blue-50 p-5 rounded-lg border border-blue-200">
                        <div className="text-3xl font-bold text-blue-600 mb-2">2027-2032</div>
                        <h4 className="font-bold text-gray-900 mb-2">Coexistência</h4>
                        <p className="text-sm text-gray-700">CIAP continua para a parcela de PIS/COFINS ainda vigente</p>
                      </div>
                      <div className="bg-yellow-50 p-5 rounded-lg border border-yellow-200">
                        <div className="text-3xl font-bold text-yellow-600 mb-2">2033</div>
                        <h4 className="font-bold text-gray-900 mb-2">Extinção Formal</h4>
                        <p className="text-sm text-gray-700">CIAP deixa de existir com o fim do PIS/COFINS</p>
                      </div>
                      <div className="bg-green-50 p-5 rounded-lg border border-green-200">
                        <div className="text-3xl font-bold text-green-600 mb-2">Pós-2033</div>
                        <h4 className="font-bold text-gray-900 mb-2">Novo Modelo</h4>
                        <p className="text-sm text-gray-700">CBS com creditamento pleno e imediato</p>
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-4 mt-8">4.2. Por Que o CIAP Deixará de Existir?</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      A CBS adotará um modelo de <strong>creditamento pleno e imediato</strong>, inspirado no IVA (Imposto sobre Valor Agregado) europeu. Isso significa que:
                    </p>

                    <div className="space-y-4 mb-6">
                      <div className="flex items-start gap-4 bg-gray-50 p-4 rounded-lg">
                        <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                        <div>
                          <h4 className="font-bold text-gray-900 mb-2">Crédito Integral Imediato</h4>
                          <p className="text-gray-700 text-sm">Os créditos sobre aquisições de ativos imobilizados poderão ser apropriados <strong>integralmente no momento da aquisição</strong>, sem necessidade de parcelamento em 48 meses.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4 bg-gray-50 p-4 rounded-lg">
                        <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                        <div>
                          <h4 className="font-bold text-gray-900 mb-2">Simplificação Administrativa</h4>
                          <p className="text-gray-700 text-sm">Eliminação da necessidade de controles complexos como o CIAP, reduzindo custos de conformidade e riscos de erros.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4 bg-gray-50 p-4 rounded-lg">
                        <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                        <div>
                          <h4 className="font-bold text-gray-900 mb-2">Neutralidade Tributária</h4>
                          <p className="text-gray-700 text-sm">O sistema de IVA busca garantir que o tributo incida apenas sobre o valor agregado, sem distorções causadas por limitações ao creditamento.</p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Créditos em Andamento */}
                  <section className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">5. O Que Acontece com os Créditos de CIAP em Andamento?</h2>
                    
                    <div className="bg-green-50 border-l-4 border-green-600 p-6 mb-6 rounded-r-lg">
                      <h3 className="text-lg font-bold text-green-900 mb-3 flex items-center gap-2">
                        <i className="ri-shield-check-line"></i>
                        Garantia de Direitos Adquiridos
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        A Lei Complementar nº 214/2025 <strong>garante expressamente</strong> que os créditos de PIS/COFINS em andamento, incluindo aqueles controlados via CIAP, serão preservados e poderão ser apropriados até o final do período de transição.
                      </p>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-4">5.1. Cenários Práticos</h3>
                    
                    <div className="space-y-6 mb-8">
                      <div className="border-l-4 border-blue-600 bg-blue-50 p-6 rounded-r-lg">
                        <h4 className="font-bold text-gray-900 mb-3">📌 Cenário 1: Ativo Adquirido em 2025</h4>
                        <p className="text-gray-700 text-sm mb-3">
                          <strong>Situação:</strong> Empresa adquire máquina em março/2025 por R$ 1.000.000, com crédito de PIS/COFINS de R$ 92.500 (9,25%).
                        </p>
                        <p className="text-gray-700 text-sm mb-3">
                          <strong>CIAP:</strong> Apropriação de R$ 1.927,08/mês durante 48 meses (março/2025 a fevereiro/2029).
                        </p>
                        <p className="text-gray-700 text-sm">
                          <strong>Impacto da Reforma:</strong> A empresa continuará apropriando os créditos mensalmente, mas a partir de 2027, apenas sobre a parcela de PIS/COFINS ainda vigente. Os créditos serão preservados até o final do período de apropriação.
                        </p>
                      </div>

                      <div className="border-l-4 border-purple-600 bg-purple-50 p-6 rounded-r-lg">
                        <h4 className="font-bold text-gray-900 mb-3">📌 Cenário 2: Ativo Adquirido em 2029</h4>
                        <p className="text-gray-700 text-sm mb-3">
                          <strong>Situação:</strong> Empresa adquire equipamento em junho/2029 por R$ 500.000.
                        </p>
                        <p className="text-gray-700 text-sm mb-3">
                          <strong>Créditos:</strong> 70% PIS/COFINS (R$ 32.375) + 30% CBS (creditamento imediato).
                        </p>
                        <p className="text-gray-700 text-sm">
                          <strong>CIAP:</strong> Será utilizado apenas para a parcela de 70% de PIS/COFINS, com apropriação em 48 meses. A parcela de 30% de CBS será creditada integralmente.
                        </p>
                      </div>

                      <div className="border-l-4 border-orange-600 bg-orange-50 p-6 rounded-r-lg">
                        <h4 className="font-bold text-gray-900 mb-3">📌 Cenário 3: Ativo com CIAP Iniciado em 2030</h4>
                        <p className="text-gray-700 text-sm mb-3">
                          <strong>Situação:</strong> Empresa inicia apropriação de CIAP em janeiro/2030, com término previsto para dezembro/2033.
                        </p>
                        <p className="text-gray-700 text-sm">
                          <strong>Tratamento:</strong> A empresa apropriará os créditos de PIS/COFINS até dezembro/2032 (quando o PIS/COFINS ainda existirá em 10%). A partir de 2033, os créditos residuais deverão ser tratados conforme regulamentação específica da Receita Federal.
                        </p>
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-4 mt-8">5.2. Tratamento dos Saldos Residuais</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Para ativos cujo período de apropriação de CIAP se estenda além de 2033, a legislação prevê três possibilidades:
                    </p>

                    <div className="grid md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-white p-5 rounded-lg border-2 border-gray-200">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                          <i className="ri-money-dollar-circle-line text-2xl text-blue-600"></i>
                        </div>
                        <h4 className="font-bold text-gray-900 mb-2">Apropriação Acelerada</h4>
                        <p className="text-sm text-gray-700">Permitir a apropriação integral dos créditos residuais em 2033</p>
                      </div>
                      <div className="bg-white p-5 rounded-lg border-2 border-gray-200">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                          <i className="ri-exchange-line text-2xl text-green-600"></i>
                        </div>
                        <h4 className="font-bold text-gray-900 mb-2">Conversão para CBS</h4>
                        <p className="text-sm text-gray-700">Converter os créditos residuais em créditos de CBS</p>
                      </div>
                      <div className="bg-white p-5 rounded-lg border-2 border-gray-200">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
                          <i className="ri-refund-line text-2xl text-purple-600"></i>
                        </div>
                        <h4 className="font-bold text-gray-900 mb-2">Ressarcimento</h4>
                        <p className="text-sm text-gray-700">Permitir pedido de ressarcimento dos créditos não apropriados</p>
                      </div>
                    </div>

                    <div className="bg-amber-50 border-l-4 border-amber-600 p-6 rounded-r-lg">
                      <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-time-line text-amber-600"></i>
                        Aguardando Regulamentação
                      </h4>
                      <p className="text-gray-700 leading-relaxed">
                        A Receita Federal ainda não publicou instrução normativa específica sobre o tratamento dos saldos residuais de CIAP. Recomenda-se acompanhar as publicações oficiais e consultar assessoria tributária especializada.
                      </p>
                    </div>
                  </section>

                  {/* Impactos Práticos */}
                  <section className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">6. Impactos Práticos para as Empresas</h2>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-4">6.1. Impactos Financeiros</h3>
                    
                    <div className="space-y-4 mb-8">
                      <div className="bg-green-50 p-5 rounded-lg border border-green-200">
                        <h4 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                          <i className="ri-arrow-up-line"></i>
                          Aspectos Positivos
                        </h4>
                        <ul className="space-y-2 text-gray-700">
                          <li className="flex items-start gap-2">
                            <i className="ri-check-line text-green-600 mt-1"></i>
                            <span><strong>Melhoria do Fluxo de Caixa:</strong> Com o creditamento imediato na CBS, as empresas terão acesso mais rápido aos créditos tributários</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-check-line text-green-600 mt-1"></i>
                            <span><strong>Redução de Capital de Giro:</strong> Menos recursos imobilizados em créditos a apropriar</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-check-line text-green-600 mt-1"></i>
                            <span><strong>Incentivo a Investimentos:</strong> Creditamento imediato estimula aquisição de ativos produtivos</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-red-50 p-5 rounded-lg border border-red-200">
                        <h4 className="font-bold text-red-900 mb-3 flex items-center gap-2">
                          <i className="ri-arrow-down-line"></i>
                          Desafios e Riscos
                        </h4>
                        <ul className="space-y-2 text-gray-700">
                          <li className="flex items-start gap-2">
                            <i className="ri-alert-line text-red-600 mt-1"></i>
                            <span><strong>Complexidade Transitória:</strong> Gestão simultânea de dois sistemas tributários (2027-2033)</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-alert-line text-red-600 mt-1"></i>
                            <span><strong>Incertezas Regulatórias:</strong> Falta de normas detalhadas sobre tratamento de créditos residuais</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-alert-line text-red-600 mt-1"></i>
                            <span><strong>Necessidade de Adaptação:</strong> Sistemas, processos e equipes precisarão ser ajustados</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-4 mt-8">6.2. Impactos Operacionais</h3>
                    
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                      <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                          <i className="ri-computer-line text-2xl text-blue-600"></i>
                        </div>
                        <h4 className="font-bold text-gray-900 mb-3">Sistemas e Tecnologia</h4>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li>• Atualização de ERPs</li>
                          <li>• Novos módulos de cálculo</li>
                          <li>• Integração com SPED</li>
                          <li>• Controles paralelos (2027-2033)</li>
                        </ul>
                      </div>

                      <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                          <i className="ri-team-line text-2xl text-purple-600"></i>
                        </div>
                        <h4 className="font-bold text-gray-900 mb-3">Pessoas e Processos</h4>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li>• Treinamento de equipes</li>
                          <li>• Revisão de procedimentos</li>
                          <li>• Consultoria especializada</li>
                          <li>• Gestão de mudanças</li>
                        </ul>
                      </div>

                      <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                          <i className="ri-file-list-line text-2xl text-green-600"></i>
                        </div>
                        <h4 className="font-bold text-gray-900 mb-3">Compliance e Documentação</h4>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li>• Auditoria de saldos de CIAP</li>
                          <li>• Documentação de créditos</li>
                          <li>• Controles internos reforçados</li>
                          <li>• Relatórios gerenciais</li>
                        </ul>
                      </div>

                      <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                        <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                          <i className="ri-line-chart-line text-2xl text-orange-600"></i>
                        </div>
                        <h4 className="font-bold text-gray-900 mb-3">Planejamento Estratégico</h4>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li>• Timing de investimentos</li>
                          <li>• Análise de viabilidade</li>
                          <li>• Gestão de fluxo de caixa</li>
                          <li>• Cenários tributários</li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* Orientações Estratégicas */}
                  <section className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">7. Orientações Estratégicas para Empresas</h2>
                    
                    <div className="bg-gradient-to-r from-red-50 to-orange-50 p-6 rounded-lg border-2 border-red-200 mb-8">
                      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-lightbulb-line text-red-600"></i>
                        Plano de Ação Recomendado
                      </h3>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        Para navegar com sucesso pela transição do CIAP e do PIS/COFINS para a CBS, recomendamos as seguintes ações:
                      </p>
                    </div>

                    <div className="space-y-6 mb-8">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-red-600 text-white rounded-lg flex items-center justify-center font-bold text-xl flex-shrink-0">1</div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-2">Auditoria Completa dos Saldos de CIAP</h4>
                          <p className="text-gray-700 text-sm mb-3">
                            Realize um levantamento detalhado de todos os ativos com CIAP em andamento, identificando:
                          </p>
                          <ul className="space-y-1 text-sm text-gray-700 ml-4">
                            <li>• Data de aquisição e início da apropriação</li>
                            <li>• Valor total do crédito e saldo remanescente</li>
                            <li>• Prazo restante de apropriação</li>
                            <li>• Ativos que terão apropriação além de 2033</li>
                          </ul>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-red-600 text-white rounded-lg flex items-center justify-center font-bold text-xl flex-shrink-0">2</div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-2">Revisão do Planejamento de Investimentos</h4>
                          <p className="text-gray-700 text-sm mb-3">
                            Avalie o timing ideal para aquisição de ativos considerando:
                          </p>
                          <ul className="space-y-1 text-sm text-gray-700 ml-4">
                            <li>• Aquisições até 2026: CIAP tradicional (48 meses)</li>
                            <li>• Aquisições 2027-2032: Sistema híbrido</li>
                            <li>• Aquisições a partir de 2033: Creditamento imediato integral</li>
                          </ul>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-red-600 text-white rounded-lg flex items-center justify-center font-bold text-xl flex-shrink-0">3</div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-2">Atualização de Sistemas e Processos</h4>
                          <p className="text-gray-700 text-sm mb-3">
                            Prepare sua infraestrutura tecnológica:
                          </p>
                          <ul className="space-y-1 text-sm text-gray-700 ml-4">
                            <li>• Contate fornecedores de ERP sobre atualizações</li>
                            <li>• Implemente controles paralelos para o período de transição</li>
                            <li>• Desenvolva relatórios gerenciais específicos</li>
                            <li>• Teste os novos cálculos antes de 2027</li>
                          </ul>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-red-600 text-white rounded-lg flex items-center justify-center font-bold text-xl flex-shrink-0">4</div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-2">Capacitação da Equipe</h4>
                          <p className="text-gray-700 text-sm mb-3">
                            Invista no desenvolvimento do time tributário:
                          </p>
                          <ul className="space-y-1 text-sm text-gray-700 ml-4">
                            <li>• Treinamentos sobre a Reforma Tributária</li>
                            <li>• Workshops práticos sobre CBS</li>
                            <li>• Simulações de cálculos no período de transição</li>
                            <li>• Atualização constante sobre normas da RFB</li>
                          </ul>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-red-600 text-white rounded-lg flex items-center justify-center font-bold text-xl flex-shrink-0">5</div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-2">Documentação e Compliance</h4>
                          <p className="text-gray-700 text-sm mb-3">
                            Reforce os controles internos:
                          </p>
                          <ul className="space-y-1 text-sm text-gray-700 ml-4">
                            <li>• Organize toda documentação de ativos e créditos</li>
                            <li>• Mantenha memórias de cálculo atualizadas</li>
                            <li>• Prepare-se para eventual fiscalização</li>
                            <li>• Considere auditoria externa preventiva</li>
                          </ul>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-red-600 text-white rounded-lg flex items-center justify-center font-bold text-xl flex-shrink-0">6</div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-2">Monitoramento Regulatório</h4>
                          <p className="text-gray-700 text-sm mb-3">
                            Acompanhe as publicações oficiais:
                          </p>
                          <ul className="space-y-1 text-sm text-gray-700 ml-4">
                            <li>• Instruções Normativas da Receita Federal</li>
                            <li>• Soluções de Consulta relevantes</li>
                            <li>• Jurisprudência administrativa (CARF)</li>
                            <li>• Manifestações de entidades de classe</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg">
                      <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-user-star-line text-blue-600"></i>
                        Consultoria Especializada
                      </h4>
                      <p className="text-gray-700 leading-relaxed">
                        Dada a complexidade da transição e as incertezas regulatórias, <strong>recomenda-se fortemente</strong> o acompanhamento de consultoria tributária especializada para análise de casos específicos, planejamento tributário e gestão de riscos.
                      </p>
                    </div>
                  </section>

                  {/* Perguntas Frequentes */}
                  <section className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">8. Perguntas Frequentes</h2>
                    
                    <div className="space-y-4">
                      <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 p-4 border-b border-gray-200">
                          <h4 className="font-bold text-gray-900 flex items-center gap-2">
                            <i className="ri-question-line text-red-600"></i>
                            Posso apropriar integralmente os créditos de CIAP antes de 2033?
                          </h4>
                        </div>
                        <div className="p-4">
                          <p className="text-gray-700 text-sm">
                            Não. Enquanto o PIS/COFINS estiver vigente (até 2032), os créditos de CIAP devem seguir a regra de apropriação em 48 meses. Apenas a partir de 2033, com a CBS em vigor integral, o creditamento será imediato. Para créditos residuais, aguarda-se regulamentação específica.
                          </p>
                        </div>
                      </div>

                      <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 p-4 border-b border-gray-200">
                          <h4 className="font-bold text-gray-900 flex items-center gap-2">
                            <i className="ri-question-line text-red-600"></i>
                            O que acontece se eu vender um ativo antes de apropriar todo o CIAP?
                          </h4>
                        </div>
                        <div className="p-4">
                          <p className="text-gray-700 text-sm">
                            Pela regra atual do PIS/COFINS, você deve estornar os créditos ainda não apropriados. Durante o período de transição, essa regra continuará valendo para a parcela de PIS/COFINS. Recomenda-se consultar a legislação específica sobre o tratamento na CBS.
                          </p>
                        </div>
                      </div>

                      <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 p-4 border-b border-gray-200">
                          <h4 className="font-bold text-gray-900 flex items-center gap-2">
                            <i className="ri-question-line text-red-600"></i>
                            Vale a pena adiar investimentos para depois de 2033?
                          </h4>
                        </div>
                        <div className="p-4">
                          <p className="text-gray-700 text-sm">
                            Depende da análise específica de cada caso. Embora o creditamento imediato seja vantajoso, adiar investimentos necessários por 8 anos pode comprometer a competitividade e eficiência operacional. Recomenda-se análise de viabilidade considerando todos os fatores (operacionais, financeiros, tributários).
                          </p>
                        </div>
                      </div>

                      <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 p-4 border-b border-gray-200">
                          <h4 className="font-bold text-gray-900 flex items-center gap-2">
                            <i className="ri-question-line text-red-600"></i>
                            Como ficam os ativos em construção (obras em andamento)?
                          </h4>
                        </div>
                        <div className="p-4">
                          <p className="text-gray-700 text-sm">
                            Ativos em construção seguem regra especial: o CIAP só inicia quando o bem é colocado em operação. Se a obra for concluída após 2033, o creditamento será imediato pela CBS. Se concluída antes, seguirá as regras de transição do PIS/COFINS.
                          </p>
                        </div>
                      </div>

                      <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 p-4 border-b border-gray-200">
                          <h4 className="font-bold text-gray-900 flex items-center gap-2">
                            <i className="ri-question-line text-red-600"></i>
                            Preciso manter os arquivos do CIAP após 2033?
                          </h4>
                        </div>
                        <div className="p-4">
                          <p className="text-gray-700 text-sm">
                            Sim. A documentação deve ser mantida pelo prazo decadencial de 5 anos, contados do primeiro dia do exercício seguinte ao da apropriação do último crédito. Isso é essencial para eventual fiscalização e comprovação dos créditos apropriados.
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Conclusão */}
                  <section className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">9. Conclusão</h2>
                    
                    <p className="text-gray-700 leading-relaxed mb-4">
                      O fim do CIAP do PIS/COFINS é uma consequência natural e inevitável da Reforma Tributária. Embora represente o encerramento de um instrumento de controle complexo e oneroso, a transição traz desafios significativos que exigem planejamento cuidadoso e atenção aos detalhes.
                    </p>

                    <div className="bg-gradient-to-r from-red-50 to-orange-50 p-6 rounded-lg border-2 border-red-200 mb-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">Principais Conclusões</h3>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <i className="ri-check-double-line text-red-600 text-xl mt-1"></i>
                          <span className="text-gray-700"><strong>Extinção Gradual:</strong> O CIAP será extinto progressivamente entre 2027 e 2033, acompanhando a transição do PIS/COFINS para a CBS</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <i className="ri-check-double-line text-red-600 text-xl mt-1"></i>
                          <span className="text-gray-700"><strong>Direitos Preservados:</strong> Créditos em andamento serão mantidos e poderão ser apropriados conforme cronograma</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <i className="ri-check-double-line text-red-600 text-xl mt-1"></i>
                          <span className="text-gray-700"><strong>Novo Modelo:</strong> A CBS trará creditamento pleno e imediato, simplificando a gestão tributária</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <i className="ri-check-double-line text-red-600 text-xl mt-1"></i>
                          <span className="text-gray-700"><strong>Planejamento Essencial:</strong> Empresas devem se preparar desde já para a transição</span>
                        </li>
                      </ul>
                    </div>

                    <p className="text-gray-700 leading-relaxed mb-4">
                      A mudança representa uma <strong>oportunidade de simplificação</strong> e melhoria do ambiente de negócios, mas exige que as empresas estejam preparadas para navegar pelo período de transição com segurança e eficiência.
                    </p>

                    <p className="text-gray-700 leading-relaxed">
                      O sucesso nessa jornada dependerá da capacidade de cada organização em <strong>planejar, adaptar-se e executar</strong> as mudanças necessárias em seus sistemas, processos e equipes. Aquelas que se anteciparem e se prepararem adequadamente estarão em posição privilegiada para aproveitar os benefícios do novo sistema tributário.
                    </p>
                  </section>

                  {/* Referências */}
                  <section className="mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">Referências Legais</h2>
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li>• Emenda Constitucional nº 132/2023</li>
                        <li>• Lei Complementar nº 214/2025</li>
                        <li>• Lei nº 10.637/2002 (PIS não-cumulativo)</li>
                        <li>• Lei nº 10.833/2003 (COFINS não-cumulativa)</li>
                        <li>• Instrução Normativa RFB nº 1.911/2019</li>
                        <li>• Receita Federal do Brasil - Portal da Reforma Tributária</li>
                      </ul>
                    </div>
                  </section>

                  {/* CTA Final */}
                  <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white p-8 rounded-xl text-center">
                    <h3 className="text-2xl font-bold mb-4">Precisa de Ajuda com a Transição?</h3>
                    <p className="text-lg mb-6 opacity-90">
                      Nossa equipe de especialistas está pronta para auxiliar sua empresa na gestão do CIAP e na preparação para a Reforma Tributária.
                    </p>
                    <Link 
                      to="/contato" 
                      className="inline-block px-8 py-4 bg-white text-red-600 font-bold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
                    >
                      Fale com Nossos Especialistas
                    </Link>
                  </div>

                </div>
              </article>

              {/* Sidebar */}
              <aside className="lg:col-span-4">
                <div className="sticky top-24 space-y-8">
                  
                  {/* Author Info */}
                  <div className="bg-white rounded-lg p-6 border border-gray-200">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                        <i className="ri-user-line text-2xl text-gray-400"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">Dr. Fernando Silva</h4>
                        <p className="text-sm text-gray-600">Especialista em Tributação Federal</p>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed mb-4">
                      Mais de 18 anos de experiência em tributação federal e planejamento tributário.
                    </p>
                    <button className="w-full px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors">
                      Ver mais artigos
                    </button>
                  </div>

                  {/* Tags */}
                  <div className="bg-white rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">CIAP</span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">PIS/COFINS</span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">CBS</span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">Reforma Tributária</span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">Crédito Tributário</span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">Ativo Imobilizado</span>
                    </div>
                  </div>
                </div>
              </aside>

            </div>
          </div>
        </div>

        {/* Feedback Banner */}
        <div className="w-full bg-white">
          <FeedbackBanner 
            articleTitle="Fim do CIAP do PIS/COFINS com a Reforma Tributária?"
            articleUrl={typeof window !== 'undefined' ? window.location.href : ''}
          />
        </div>

      </div>
      
      <Footer />
    </>
  );
}
